import { Field, Form, Formik, useFormikContext } from 'formik'
import { use, useEffect, useState } from 'react'
import _ from 'lodash'
import {
  Box,
  Button,
  ButtonGroupActions,
  CheckboxGroup,
  Divider,
  FormControl,
  HStack,
  ModalDialog,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  Tooltip,
  useErrorModal,
  useSuccessToast
} from '~/design_system'
import { UserPreferenceType, VisibleFieldData, VisibleFieldPosition } from '~/api/userPreferences'
import { ApplicationError } from '~/common/fetch'
import { useActionBarContext } from '~/components/v2/ResultsLayout/ActionBar/ActionBar.hook'
import { Msg, useMsg } from '~/common/localizationUtils'
import { CheckboxWithDisabledTooltip } from '~/components/v2/WithDisabledTooltip'
import { getGrayIcon, ListCardViewFieldSettingsContext } from './ListViewDefaultsApp'

type CustomizeDefaultDataModalProps = {
  isOpen?: boolean
  onClose?: () => void
  initialValues?: VisibleFieldData[]
  applyAction?: (data: any) => Promise<any>
  userApplyOnChange?: (visibleFields: VisibleFieldData[]) => void
  onSuccess?: () => void
}

export const CustomizeDefaultDataModal = ({
  isOpen: isOpenFromOverride = false,
  onClose: onCloseFromOverride = undefined,
  initialValues: initialValuesFromOverride,
  applyAction,
  userApplyOnChange,
  onSuccess
}: CustomizeDefaultDataModalProps) => {
  const msg = useMsg()
  const { showModal: showErrorModal } = useErrorModal()
  const successToast = useSuccessToast()
  const MAX_FIELDS = 6
  const [error, setErrorMessage] = useState('')
  const [limitReached, setLimitReached] = useState(false)
  // action bar context
  const { isOpen: isOpenFromActionBarContext, onClose: onCloseFromActionBarContext } =
    useActionBarContext()?.visibleFieldsDisclosure ?? {}
  const namespaceFromActionBarContext = useActionBarContext()?.userPreferenceNamespace ?? {}
  // settings context
  const {
    userRole,
    namespace: namespaceFromSettingsContext,
    visibleFields: initialValuesFromSettingsContext,
    setVisibleFields
  } = use(ListCardViewFieldSettingsContext) ?? {}
  const isDefaultDataSettingsMode = userRole ?? false
  const isOpen = isOpenFromOverride || isOpenFromActionBarContext || false
  const onClose = onCloseFromOverride ?? onCloseFromActionBarContext
  const initialValues = initialValuesFromSettingsContext ?? initialValuesFromOverride
  const attrColFieldKeys = initialValues
    ?.filter(visibleField => visibleField.position === VisibleFieldPosition.ATTRIBUTES_COLUMN)
    .map(visibleField => visibleField.key)
  const enabledInAttrCol = (field: VisibleFieldData) =>
    field.value && field.position === VisibleFieldPosition.ATTRIBUTES_COLUMN
  const titleAdditionalInfoFieldsFilter = (field: VisibleFieldData) =>
    !field.blocked && field.position === VisibleFieldPosition.TITLE_ADDITIONAL_INFO
  const attributeColumnFieldsFilter = (field: VisibleFieldData) =>
    !field.blocked && field.position === VisibleFieldPosition.ATTRIBUTES_COLUMN
  const metaFieldsFilter = (field: VisibleFieldData) => !field.blocked && field.position === VisibleFieldPosition.META
  const initialLimitReached = initialValues?.filter(enabledInAttrCol).length === MAX_FIELDS
  const hasTitleAdditionalInfoFields = initialValues?.filter(titleAdditionalInfoFieldsFilter).length > 0
  const hasMetaFields = initialValues?.filter(metaFieldsFilter).length > 0
  const orderByEnabledAndDisplay = (values: VisibleFieldData[]) =>
    _.orderBy(values, ['value', 'display_order'], ['desc'])

  useEffect(() => {
    /*
      eslint-disable-next-line react-hooks/set-state-in-effect --

      ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
      Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
      There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
      couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

      Issue: Syncing limitReached from prop causes extra render.
      Possible fix approach: Initialize state from prop:
        const [limitReached, setLimitReached] = useState(initialLimitReached)
      Or use the prop directly.
    */
    setLimitReached(initialLimitReached)
  }, [initialValues, initialLimitReached])

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={() => {
        if (onClose) onClose()
        setLimitReached(initialLimitReached)
        setErrorMessage('')
      }}
      closeOnOverlayClick={false}>
      <ModalDialogHeader>
        <Msg id="settings.list_card_view_defaults.customize_default_data.modal.title" />
      </ModalDialogHeader>
      <Formik
        initialValues={initialValues || []}
        onSubmit={async (values, actions) => {
          try {
            // if we're setting tenant-level settings (provided a viewType of employer or vendor),
            // on settings page: ListViewDefaultsApp vs. using userApplyAction to set user-level settings.
            // User roles are derived from their user account on the server. Current usages: JobListPage
            const fieldData = isDefaultDataSettingsMode
              ? {
                  namespace: namespaceFromSettingsContext,
                  userRole,
                  visibleFields: values
                }
              : {
                  namespace: namespaceFromActionBarContext,
                  preference_type: UserPreferenceType.VISIBLE_FIELDS,
                  data: values
                }
            if (applyAction) {
              await applyAction(fieldData)
            }
            if (isDefaultDataSettingsMode) {
              setVisibleFields(values)
            } else {
              userApplyOnChange!(values)
            }
            if (onSuccess) onSuccess()
            if (onClose) onClose()
            successToast(
              msg('settings.list_card_view_defaults.default_data.update_success_text', {
                data: msg('settings.list_card_view_defaults.current_data')
              })
            )
          } catch (err) {
            if (err instanceof ApplicationError) {
              showErrorModal({ message: err?.body?.non_field_errors })
            } else {
              showErrorModal({})
            }
            actions.setSubmitting(false)
          }
        }}>
        {({ values, isSubmitting, handleSubmit }) => (
          <>
            <ModalDialogBody>
              <Form>
                <Stack shouldWrapChildren spacing={6}>
                  {values && values?.length && (
                    <Field name="visible_fields">
                      {() => (
                        <FormControl>
                          <Msg id="settings.list_card_view_defaults.customize_default_data.modal.helper_text" />
                          {hasTitleAdditionalInfoFields && (
                            <Stack my="lg">
                              {orderByEnabledAndDisplay(values)
                                .filter(titleAdditionalInfoFieldsFilter)
                                .map(visibleField => (
                                  <NonAttributeColumnField key={visibleField.key} visibleField={visibleField} />
                                ))}
                              <Divider />
                            </Stack>
                          )}
                          <CheckboxGroup
                            defaultValue={values.filter(enabledInAttrCol).map(visibleField => visibleField.key)}
                            onChange={checkedValues => {
                              setErrorMessage('')
                              setLimitReached(false)
                              const attrColFieldsLength = checkedValues?.filter(c =>
                                attrColFieldKeys.includes(c as string)
                              ).length
                              if (attrColFieldsLength < 1) {
                                setErrorMessage(
                                  msg(
                                    'settings.list_card_view_defaults.customize_default_data.modal.error.at_least_one_field'
                                  )
                                )
                              } else if (attrColFieldsLength >= MAX_FIELDS) {
                                setLimitReached(true)
                              }
                            }}>
                            <Stack my="lg">
                              {orderByEnabledAndDisplay(values)
                                .filter(attributeColumnFieldsFilter)
                                .map(visibleField => (
                                  <AttributeColumnField
                                    key={visibleField.key}
                                    visibleField={visibleField}
                                    selectedLimitReached={limitReached && !visibleField.value}
                                  />
                                ))}
                            </Stack>
                          </CheckboxGroup>
                          {hasMetaFields && (
                            <Stack my="lg">
                              {orderByEnabledAndDisplay(values)
                                .filter(metaFieldsFilter)
                                .map(visibleField => (
                                  <NonAttributeColumnField key={visibleField.key} visibleField={visibleField} />
                                ))}
                            </Stack>
                          )}
                        </FormControl>
                      )}
                    </Field>
                  )}
                </Stack>
              </Form>
            </ModalDialogBody>
            <ModalDialogFooter>
              <ButtonGroupActions>
                <Tooltip isDisabled={!error} label={error}>
                  <Box>
                    <Button
                      isDisabled={error !== ''}
                      isLoading={isSubmitting}
                      variant="primary"
                      onClick={() => {
                        handleSubmit()
                      }}>
                      <Msg id="common.ui.apply" />
                    </Button>
                  </Box>
                </Tooltip>
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (onClose) onClose()
                    setLimitReached(initialLimitReached)
                    setErrorMessage('')
                  }}
                  isDisabled={isSubmitting}>
                  <Msg id="common.ui.cancel" />
                </Button>
              </ButtonGroupActions>
              <Text position="absolute" right={6}>
                {values?.filter(enabledInAttrCol)?.length}{' '}
                {msg('settings.list_card_view_defaults.customize_default_data.modal.num_data_selected')}
              </Text>
            </ModalDialogFooter>
          </>
        )}
      </Formik>
    </ModalDialog>
  )
}

const AttributeColumnField = ({
  visibleField,
  selectedLimitReached
}: {
  visibleField: VisibleFieldData
  selectedLimitReached: boolean
}) => {
  const { values, setValues } = useFormikContext<VisibleFieldData[]>()
  return (
    <CheckboxWithDisabledTooltip
      disabledTooltip={{
        label: <Msg id="settings.list_card_view_defaults.customize_default_data.modal.error.max_reached" />
      }}
      value={visibleField.key}
      isChecked={visibleField.value === true}
      isDisabled={selectedLimitReached}
      onChange={e => {
        setValues([...values.filter(vf => vf.key !== visibleField.key), { ...visibleField, value: e.target.checked }])
      }}
      mr={2}>
      <HStack my={1}>
        {getGrayIcon(visibleField.icon)}
        <Text>{visibleField.label}</Text>
      </HStack>
    </CheckboxWithDisabledTooltip>
  )
}

const NonAttributeColumnField = ({ visibleField }: { visibleField: VisibleFieldData }) => {
  const { values, setValues } = useFormikContext<VisibleFieldData[]>()

  if (visibleField.options) {
    return (
      <RadioGroup
        value={visibleField.value as string}
        onChange={value => {
          setValues([...values.filter(vf => vf.key !== visibleField.key), { ...visibleField, value: value.toString() }])
        }}>
        <Text>{visibleField.label}</Text>
        <Stack direction="column" mt={6} spacing={4}>
          {visibleField.options?.map(option => (
            <Radio key={option.value} value={option.value}>
              {option.label}
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
    )
  }
  return (
    <Stack isInline justify="space-between" my="5px">
      <Stack isInline spacing={2}>
        {getGrayIcon(visibleField.icon)}
        <Text>{visibleField.label}</Text>
      </Stack>
      <Switch
        value={visibleField.key}
        isChecked={visibleField.value === true}
        onChange={e => {
          setValues([...values.filter(vf => vf.key !== visibleField.key), { ...visibleField, value: e.target.checked }])
        }}
      />
    </Stack>
  )
}
